// ── Orchestrateur traductions ─────────────────────────────────────────────────
//
// Ce module s'abonne à l'événement 'usito:recherche' déclenché par usito.js.
// Il est conçu pour fonctionner indépendamment de usito.js — aucun couplage direct.
//
// Activation : gérée par le flag BETA (window.beta === true).
// Chargement : via l'entry point usito-paquet.js (toutes les pages).
//
// Source de vérité des langues disponibles : PAIRES_TRAD (ci-dessous).
// Pour ajouter une langue : ajouter une entrée ici, rien d'autre à modifier.

import { lirePrefsTraductions, sauvegarderPrefsTraductions } from './prefs.js';
import { getTraductionsResults } from './api.js';
import { créerLigneSuggestion, updateTradLigneHTML, afficherSectionTraductions } from './ui.js';

/**
 * Paires de traduction disponibles.
 * clé   : "src-dst" — identifiant utilisé dans localStorage, les IDs DOM et les URLs API
 * label : affiché dans les préférences (en français)
 *
 * Doit rester synchronisé avec l'enum Langue dans matrice-java-rest.
 * Convention : "xx-fr" = autre langue ⮕ français  /  "fr-xx" = français ⮕ autre langue
 */
export const PAIRES_TRAD = [
    // ── Vers le français ──────────────────────────────────────────────────────
    { clé: "en-fr", label: "anglais ⮕ français"     },
    { clé: "ar-fr", label: "arabe ⮕ français"       },
    { clé: "es-fr", label: "espagnol ⮕ français"    },
    { clé: "it-fr", label: "italien ⮕ français"     },
    { clé: "ht-fr", label: "créole haïtien ⮕ français"     },
    { clé: "zh-fr", label: "mandarin ⮕ français"    },
    { clé: "el-fr", label: "grec ⮕ français"        },
    { clé: "pt-fr", label: "portugais ⮕ français"   },
    { clé: "ro-fr", label: "roumain ⮕ français"     },
    { clé: "ru-fr", label: "russe ⮕ français"       },
    { clé: "uk-fr", label: "ukrainien ⮕ français"   },
    { clé: "vi-fr", label: "vietnamien ⮕ français"  },
    { clé: "pa-fr", label: "penjabi ⮕ français"  },
    { clé: "fil-fr", label: "filipino ⮕ français"  },
    { clé: "nl-fr", label: "néerlandais ⮕ français" },
    // ── Depuis le français ────────────────────────────────────────────────────
    { clé: "fr-en", label: "français ⮕ anglais"     },
    { clé: "fr-ar", label: "français ⮕ arabe"       },
    { clé: "fr-es", label: "français ⮕ espagnol"    },
    { clé: "fr-it", label: "français ⮕ italien"     },
    { clé: "fr-ht", label: "français ⮕ créole haïtien"},
    { clé: "fr-zh", label: "français ⮕ mandarin"    },
    { clé: "fr-el", label: "français ⮕ grec"        },
    { clé: "fr-pt", label: "français ⮕ portugais"   },
    { clé: "fr-ro", label: "français ⮕ roumain"     },
    { clé: "fr-ru", label: "français ⮕ russe"       },
    { clé: "fr-uk", label: "français ⮕ ukrainien"   },
    { clé: "fr-vi", label: "français ⮕ vietnamien"  },
    { clé: "fr-pa", label: "français ⮕ penjabi"  },
    { clé: "fr-fil", label: "français ⮕ filipino"  },
    { clé: "fr-nl", label: "français ⮕ néerlandais" },
];

/**
 * Génère les lignes de suggestion dans #dsr-traductions.
 * Appelé une seule fois au chargement ; les divs n'existent pas dans le HTML.
 * Crée les divs pour les paires fixes (PAIRES_TRAD) ET pour les paires custom
 * déjà stockées dans localStorage.
 */
function initialiserLignesSuggestions() {
    const conteneur = document.getElementById('dsr-traductions');
    if (!conteneur) return;

    const clésConnues = new Set(PAIRES_TRAD.map(function(p) { return p.clé; }));

    // Paires fixes (le label long alimente l'infobulle de la ligne)
    PAIRES_TRAD.forEach(function({ clé, label }) { créerLigneSuggestion(conteneur, clé, label); });

    // Paires custom stockées en prefs mais absentes de PAIRES_TRAD (pas de label → la clé)
    Object.keys(lirePrefsTraductions()).forEach(function(clé) {
        if (!clésConnues.has(clé)) créerLigneSuggestion(conteneur, clé);
    });
}

/**
 * Émet le signal de fin des traductions consommé par prononciations/index.js.
 * @param {string} texte mot recherché
 * @param {Array<{lemme:string, lexieId:string}>} traductionsVersFr  traductions vers le fr (peut être vide)
 */
function signalerFinTraductions(texte, traductionsVersFr) {
    document.dispatchEvent(new CustomEvent('usito:traductions-terminées', {
        detail: { texte: texte, traductionsVersFr: traductionsVersFr }
    }));
}

/**
 * Retourne TOUTES les traductions VERS LE FRANÇAIS ({ lemme, lexieId }) trouvées
 * parmi les résultats des paires actives (toutes langues xx→fr confondues),
 * dédupliquées par lemme (un même mot français prononcé une seule fois, quelle
 * que soit la langue source ou le nombre de lexies homonymes). Ordre d'apparition conservé.
 */
function collecteTraductionsVersFr(résultats) {
    const parLemme = new Map(); // lemme → { lemme, lexieId }
    for (const r of résultats) {
        const versFr = r.clé.split('-')[1] === 'fr';
        if (versFr && r.ok && r.valeur && r.valeur.données) {
            for (const lexie of r.valeur.données) {
                if (lexie.lemme && lexie.lexieId && !parLemme.has(lexie.lemme)) {
                    parLemme.set(lexie.lemme, { lemme: lexie.lemme, lexieId: lexie.lexieId });
                }
            }
        }
    }
    return [...parLemme.values()];
}

/**
 * Lance les appels de traduction en parallèle pour les paires activées
 * dans les préférences, et met à jour chaque ligne de résultats.
 * @param {string} searchText  mot saisi par l'utilisateur
 */
function lancerSuggestionsTraductions(searchText) {
    const prefs      = lirePrefsTraductions();
    const clésFixées = new Set(PAIRES_TRAD.map(function(p) { return p.clé; }));

    // Paires fixes activées + paires custom activées (absentes de PAIRES_TRAD)
    const pairesActives = [
        ...PAIRES_TRAD.filter(function({ clé }) { return prefs[clé] === true; }),
        ...Object.entries(prefs)
            .filter(function([clé, actif]) { return actif && !clésFixées.has(clé); })
            .map(function([clé]) { return { clé: clé }; }),
    ];

    // Masquer immédiatement toutes les lignes présentes dans le DOM qui ne sont pas actives
    const clésActives = new Set(pairesActives.map(function(p) { return p.clé; }));
    document.querySelectorAll('[id^="dsr-trad-"]').forEach(function(el) {
        const clé = el.id.replace('dsr-trad-', '');
        if (!clésActives.has(clé)) el.style.display = 'none';
    });

    // Aucune paire active : masquer toute la section (label compris) et signaler
    // l'absence de traduction vers le fr à prononciations/index.js.
    if (pairesActives.length === 0) {
        afficherSectionTraductions(false);
        signalerFinTraductions(searchText, []);
        return;
    }

    const promesses = pairesActives.map(function({ clé }) {
        const [src, dst] = clé.split('-');
        return getTraductionsResults(searchText, src, dst)
            .then(function(résultat) { return { ok: true, valeur: résultat, clé: clé }; })
            .catch(function(raison)  { return { ok: false, raison: raison,  clé: clé }; });
    });

    Promise.all(promesses).then(function(résultats) {
        résultats.forEach(function(résultat) {
            const clé = résultat.clé;
            if (résultat.ok) {
                const lexies = résultat.valeur && résultat.valeur.données
                    ? résultat.valeur.données
                    : [];
                updateTradLigneHTML(clé, lexies);
            } else {
                console.error('Erreur traductions ' + clé + ' :', résultat.raison);
                updateTradLigneHTML(clé, []);
            }
        });

        // Toutes les promesses sont terminées : n'afficher la section que si
        // au moins une paire a retourné une traduction, sinon la masquer.
        const auMoinsUneTraduction = résultats.some(function(r) {
            return r.ok && r.valeur && r.valeur.données && r.valeur.données.length > 0;
        });
        afficherSectionTraductions(auMoinsUneTraduction);

        // Signal pour prononciations/index.js : toutes les traductions vers le fr
        // ({ lemme, lexieId }), dédupliquées par lemme (liste éventuellement vide).
        signalerFinTraductions(searchText, collecteTraductionsVersFr(résultats));
    });
}

// Exposer sur window pour les scripts inline de préférences.html :
// - PAIRES_TRAD  : pour générer les checkboxes
// - lirePrefsTraductions / sauvegarderPrefsTraductions : pour les lire/écrire
// Ces exports doivent être disponibles même si le flag BETA est inactif.
window['PAIRES_TRAD']              = PAIRES_TRAD;
window.lirePrefsTraductions        = lirePrefsTraductions;
window.sauvegarderPrefsTraductions = sauvegarderPrefsTraductions;
window.créerLigneSuggestion        = créerLigneSuggestion;

// N'activer le module que si le flag BETA est actif.
// (window.beta est injecté par Thymeleaf dans head.html via togglz:active="BETA")
if (typeof window.beta !== 'undefined' && window.beta === true) {
    document.addEventListener('DOMContentLoaded', initialiserLignesSuggestions);
    document.addEventListener('usito:recherche', function({ detail }) {
        lancerSuggestionsTraductions(detail.texte);
    });
}
